/* ==========================================================================
   Homepage banner slider — sections/homepage-banner-slider.liquid

   这个文件存在的唯一理由是：多个视频 slide 的播放和下载必须被管住。
   纯 Swiper + 内联 <source> 会导致
     - PC / 移动两套源同时下载（旧 banner 就有这个问题，靠 CSS display 切换）
     - 所有 slide 的视频在首屏一起抢带宽
     - 非当前 slide 的视频在后台继续解码
   所以 <source> 一律由这里按「当前视口 + 当前 slide」动态挂载。
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 960px)';
  var instances = [];

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * 按实际显示宽度挑清晰度档。
   *
   * Shopify 给每个视频转出多档，而且 SD 排在最前 —— 比如联名那条
   * Joint_cooperation_banner_pc.mp4 的顺序是 960x480 / 1440x720 / m3u8。
   * <video> 选 source 的规则是「按顺序取第一个能播的」，**不比较画质**，
   * 所以原样输出就永远命中 960x480，全宽拉到 1440+ 就糊了
   * （联名页看着清楚是因为那边的设置是 text 类型、直接贴原始文件 URL，
   *  根本不走转码档位）。
   *
   * 这里按 容器CSS宽度 x DPR 选第一个够用的 mp4，没有够用的就选最大的。
   * m3u8 追加在最后：Chrome 不支持原生 HLS 会跳过，Safari 只有在前面
   * 所有 mp4 都不能播时才会用到它。
   */
  function pickSources(list, cssWidth) {
    var need = Math.round((cssWidth || 1440) * (window.devicePixelRatio || 1));
    var mp4 = [];
    var rest = [];
    list.forEach(function (s) {
      if (!s || !s.src) return;
      (s.type === 'video/mp4' ? mp4 : rest).push(s);
    });
    mp4.sort(function (a, b) {
      return (a.w || 0) - (b.w || 0);
    });

    var chosen = null;
    for (var i = 0; i < mp4.length; i++) {
      if ((mp4[i].w || 0) >= need) {
        chosen = mp4[i];
        break;
      }
    }
    if (!chosen && mp4.length) chosen = mp4[mp4.length - 1];

    var out = [];
    if (chosen) out.push(chosen);
    // 其余 mp4 按分辨率从高到低兜底，最后才是 m3u8
    mp4.slice().reverse().forEach(function (s) {
      if (s !== chosen) out.push(s);
    });
    return out.concat(rest);
  }

  function prefersLessData() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return /^(slow-2g|2g)$/.test(conn.effectiveType || '');
  }

  function BannerSlider(root) {
    var self = this;
    this.root = root;
    this.viewport = root.querySelector('.swiper');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-hb-slide]'));
    this.toggle = root.querySelector('[data-hb-toggle]');
    this.mobileMQ = window.matchMedia(MOBILE_QUERY);

    this.autoplayEnabled = root.dataset.hbAutoplay === 'true' && this.slides.length > 1;
    this.delay = parseInt(root.dataset.hbDelay, 10) || 6000;

    // 低数据 / 减少动效：不加载任何视频，只留海报图，也不自动轮播。
    this.mediaAllowed = !prefersReducedMotion() && !prefersLessData();
    if (!this.mediaAllowed) this.autoplayEnabled = false;

    this.pausedByUser = false;
    this.hovered = false;
    // undefined = 还没跑过 syncMedia
    this.running = undefined;
    // undefined = IntersectionObserver 还没回调过，先按可见处理，别让首屏卡住
    this.inView = undefined;

    this.onSlideChange = this.syncMedia.bind(this);
    this.onViewportChange = function () {
      // 跨断点等于换了一条在放的视频。把当前 slide 的两条都归零，
      // 并把 running 置回 false 逼出 syncMedia 的 resuming 分支
      // （视频归零 + autoplay 先 stop 再 start），
      // 否则新视口的视频从旧进度续播，而计时器又从完整时长重新开始。
      var slide = self.slides[self.activeIndex()];
      if (slide) {
        var vids = slide.querySelectorAll('[data-hb-video]');
        Array.prototype.forEach.call(vids, function (v) {
          if (!v.paused) v.pause();
          if (v.currentTime > 0) {
            try {
              v.currentTime = 0;
            } catch (e) {
              /* 元数据未就绪，忽略 */
            }
          }
        });
      }
      self.running = false;
      self.reapplyDurations();
      self.updateToggleVisibility();
      self.syncMedia();
    };
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);

    this.initSwiper();
    this.bindDragGuard();
    this.bindHover();
    this.bindToggle();
    this.bindViewportChange();
    this.observeVisibility();
    this.syncMedia();

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* ---------------------------------------------------------------- Swiper */

  BannerSlider.prototype.initSwiper = function () {
    if (this.slides.length < 2 || typeof window.Swiper === 'undefined') {
      // 单 slide 或 Swiper 没加载出来：DOM 本身就是可用的静态 banner。
      return;
    }

    var root = this.root;
    var options = {
      slidesPerView: 1,
      spaceBetween: 0,
      // 刻意不用 loop：Swiper 8 的 loop 会克隆 slide，
      // 克隆出来的 <video> 会让视频下载和播放控制全部翻倍。
      loop: false,
      rewind: root.dataset.hbRewind === 'true',
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous banner',
        nextSlideMessage: 'Next banner'
      },
      // 刻意不用 pauseOnMouseEnter —— 它只停 Swiper 的计时器，视频照播，
      // 鼠标移开后计时器从头走完整时长而视频已经播到一半，两者失步。
      // 悬停由 bindHover() 统一走 syncMedia，视频和计时器一起停、一起重来。
      autoplay: this.autoplayEnabled
        ? { delay: this.delay, disableOnInteraction: false }
        : false
    };

    // 控件刻意不用 swiper 的默认类名。base.css 里有一整套全局
    // .swiper-button-next / .swiper-pagination .swiper-pagination-bullet 覆盖
    // （伪元素圆点、position:static、hover 时 translateX 顶掉垂直居中），
    // 用自己的类名就彻底躲开这场优先级battle。
    if (root.dataset.hbPagination === 'true') {
      options.pagination = {
        el: root.querySelector('.hbSlider__pagination'),
        clickable: true,
        bulletClass: 'hbSlider__dot',
        bulletActiveClass: 'hbSlider__dot--active',
        clickableClass: 'hbSlider__pagination--clickable'
      };
    }

    if (root.dataset.hbNavigation === 'true') {
      options.navigation = {
        nextEl: root.querySelector('.hbSlider__arrow--next'),
        prevEl: root.querySelector('.hbSlider__arrow--prev'),
        disabledClass: 'hbSlider__arrow--disabled',
        lockClass: 'hbSlider__arrow--lock'
      };
    }

    this.swiper = new window.Swiper(this.viewport, options);
    this.swiper.on('slideChange', this.onSlideChange);
  };

  BannerSlider.prototype.activeIndex = function () {
    return this.swiper ? this.swiper.activeIndex : 0;
  };

  /* ----------------------------------------------------------------- Media */

  BannerSlider.prototype.videoFor = function (slide) {
    if (!this.mediaAllowed) return null;
    var selector = this.mobileMQ.matches ? '[data-hb-video="mobile"]' : '[data-hb-video="desktop"]';
    return slide.querySelector(selector);
  };

  BannerSlider.prototype.attach = function (video) {
    if (!video || video.dataset.hbAttached === '1') return;

    var sources;
    try {
      sources = JSON.parse(video.dataset.hbSources || '[]');
    } catch (e) {
      sources = [];
    }
    if (!sources.length) return;

    // 按这条视频实际的显示宽度挑档，别让浏览器命中排在最前的 SD
    var box = video.getBoundingClientRect().width || video.offsetWidth;
    sources = pickSources(sources, box);

    sources.forEach(function (source) {
      if (!source || !source.src) return;
      var el = document.createElement('source');
      el.src = source.src;
      if (source.type) el.type = source.type;
      video.appendChild(el);
    });

    video.dataset.hbAttached = '1';
    video.load();
    this.bindAutoDuration(video);
  };

  /**
   * duration 留 0 的视频 slide：用视频自身长度当停留时间。
   * 手填秒数太容易和视频对不上（现网素材 15.6s，填 8s 就播一半被切走）。
   * Swiper 每次排下一次切换时都会重读 active slide 的 data-swiper-autoplay，
   * 所以写回属性即可；如果写的正好是当前屏，重启一次 autoplay 让它立刻生效。
   */
  BannerSlider.prototype.bindAutoDuration = function (video) {
    var slide = video.closest('[data-hb-slide]');
    if (!slide || slide.dataset.hbAutoDuration !== 'true') return;
    if (video.dataset.hbDurationBound === '1') return;
    video.dataset.hbDurationBound = '1';

    var self = this;
    var apply = function () {
      // 跨 960px 断点来回切时，桌面和移动两条视频都可能触发 loadedmetadata。
      // 只让「当前视口真正在放的那条」写回时长，否则最后触发的那条获胜，
      // 写进去的可能是另一个视口的视频长度。
      if (video !== self.videoFor(slide)) return;
      if (!isFinite(video.duration) || video.duration <= 0) return;
      var ms = Math.round(video.duration * 1000);
      if (slide.dataset.swiperAutoplay === String(ms)) return;
      slide.dataset.swiperAutoplay = String(ms);

      var sw = self.swiper;
      if (sw && sw.autoplay && sw.autoplay.running && self.slides[sw.activeIndex] === slide) {
        sw.autoplay.stop();
        sw.autoplay.start();
      }
    };

    // 不能用 once —— 跨断点后需要重新判定哪条视频说了算。
    video.addEventListener('loadedmetadata', apply);
    if (video.readyState >= 1) apply();
    this._durationAppliers = this._durationAppliers || [];
    this._durationAppliers.push(apply);
  };

  /** 视口变化后重新裁决由哪条视频决定停留时长 */
  BannerSlider.prototype.reapplyDurations = function () {
    (this._durationAppliers || []).forEach(function (fn) {
      fn();
    });
  };

  BannerSlider.prototype.play = function (video) {
    if (!video) return;
    this.attach(video);
    if (video.dataset.hbAttached !== '1') return;

    var attempt = function () {
      var promise = video.play();
      // 自动播放被拦截不是错误，海报图会继续顶着。
      if (promise && promise.catch) promise.catch(function () {});
    };

    attempt();

    // attach() 刚调过 load()，此时 play() 可能被这次 load 打断。
    // 数据到位后再补一次，否则首屏视频会停在海报帧上。
    if (video.readyState < 2 && !video.dataset.hbRetry) {
      video.dataset.hbRetry = '1';
      video.addEventListener(
        'loadeddata',
        function () {
          if (video.dataset.hbShouldPlay === '1') attempt();
        },
        { once: true }
      );
    }
  };

  /**
   * 单一真相来源：当前 slide 的视频播放，其余全部暂停并回到第一帧；
   * 下一个 slide 的视频提前挂载但不播，滑过去时不会黑一下。
   */
  BannerSlider.prototype.syncMedia = function () {
    var self = this;
    var active = this.activeIndex();

    // 分两档，别混成一个判据：
    //
    // 视频该不该播 —— 用户点暂停 / 标签页切走 / 滚出视口。
    // 悬停**不在其中**：悬停的本意是「别在我要点的时候切走」，不是
    // 「停止播放内容」。以前把悬停也算进来，结果鼠标扫过 banner 一下，
    // 视频就被暂停、移开又从 0 重播（实测进度 5.13s -> 1.47s），很突兀；
    // 而且那一刻按钮还显示着「暂停」图标，和实际状态对不上。
    var videoShouldRun = !this.pausedByUser && !document.hidden && this.inView !== false;

    // 轮播计时器该不该跑 —— 在上面基础上再加悬停。
    var carouselShouldRun = videoShouldRun && !this.hovered;

    // 只有视频那一档从「停」恢复时才归零重来（配合 autoplay 重启，两者同起点）。
    // 悬停不触发这个分支，所以移开后视频是接着播的。
    // 注：Swiper 的 autoplay 无论 stop/start 还是 pause/run，恢复后都是重新
    // 计满一个 delay，拿不到剩余时间；所以悬停后计时器会重新计满，
    // 视频则继续 —— 两者短暂错开，但视频是循环的，观感上无缝。
    var resuming = videoShouldRun && this.running === false;
    this.running = videoShouldRun;
    var shouldRun = videoShouldRun;

    this.slides.forEach(function (slide, index) {
      var videos = slide.querySelectorAll('[data-hb-video]');
      Array.prototype.forEach.call(videos, function (video) {
        var isCurrentViewport = video === self.videoFor(slide);
        var keepPlaying = shouldRun && index === active && isCurrentViewport;

        video.dataset.hbShouldPlay = keepPlaying ? '1' : '0';
        if (keepPlaying) return;

        if (!video.paused) video.pause();
        // 非当前 slide 回到第一帧，下次切回来从头播，节奏一致。
        if (index !== active && video.currentTime > 0) {
          try {
            video.currentTime = 0;
          } catch (e) {
            /* 元数据还没就绪，忽略 */
          }
        }
      });
    });

    if (this.swiper && this.swiper.autoplay && this.autoplayEnabled) {
      if (carouselShouldRun) {
        // 先 stop 再 start，让计时器和刚归零的视频从同一刻开始
        if (resuming && this.swiper.autoplay.running) this.swiper.autoplay.stop();
        if (!this.swiper.autoplay.running) this.swiper.autoplay.start();
      } else if (this.swiper.autoplay.running) {
        this.swiper.autoplay.stop();
      }
    }

    if (!shouldRun) return;

    var activeSlide = this.slides[active];
    if (activeSlide) {
      var activeVideo = this.videoFor(activeSlide);
      if (resuming && activeVideo && activeVideo.currentTime > 0) {
        try {
          activeVideo.currentTime = 0;
        } catch (e) {
          /* 元数据未就绪，忽略 */
        }
      }
      this.play(activeVideo);
    }

    var next = this.slides[active + 1];
    if (next) this.attach(this.videoFor(next));
  };

  BannerSlider.prototype.bindViewportChange = function () {
    var handler = this.onViewportChange;
    if (this.mobileMQ.addEventListener) {
      this.mobileMQ.addEventListener('change', handler);
    } else if (this.mobileMQ.addListener) {
      this.mobileMQ.addListener(handler);
    }
  };

  BannerSlider.prototype.handleVisibilityChange = function () {
    // 标签页切走 / 切回都由 syncMedia 一处收口，避免两套暂停逻辑打架。
    this.syncMedia();
  };

  /**
   * 鼠标悬停时视频和轮播一起停（替代 Swiper 的 pauseOnMouseEnter，
   * 那个只停计时器不停视频）。移开后由 syncMedia 统一从头重启。
   */
  BannerSlider.prototype.bindHover = function () {
    var self = this;
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.root.addEventListener('mouseenter', function () {
      self.hovered = true;
      self.syncMedia();
    });
    this.root.addEventListener('mouseleave', function () {
      self.hovered = false;
      self.syncMedia();
    });
  };

  /* --------------------------------------------------------------- Controls */

  /**
   * 只有「确实有东西会自己动」时才显示暂停按钮。
   * Liquid 判断不了 reduced-motion / Save-Data，所以按钮默认是 hidden，
   * 由这里放出来 —— 否则会出现一个点了没有任何效果、标签还在
   * Pause/Play 之间来回跳的误导性控件。
   */
  /**
   * 当前视口下是不是真的有视频会自动播。
   * 不能只看 DOM 里有没有 <video> —— 移动端 + mobile_video_policy=none 时，
   * 桌面视频仍在 DOM 里（display:none），照那样判断会显示一个点了没反应的按钮。
   * videoFor() 已经同时考虑了当前视口和 mediaAllowed（reduced-motion / Save-Data）。
   */
  BannerSlider.prototype.hasPlayableVideo = function () {
    var self = this;
    for (var i = 0; i < this.slides.length; i++) {
      if (self.videoFor(this.slides[i])) return true;
    }
    return false;
  };

  /** 只有「确实有东西会自己动」时才显示暂停按钮；视口变化后要重判 */
  BannerSlider.prototype.updateToggleVisibility = function () {
    if (!this.toggle) return;
    // 隐藏而不是移除：跨断点回来时还要能再显示出来
    this.toggle.hidden = !(this.autoplayEnabled || this.hasPlayableVideo());
  };

  BannerSlider.prototype.bindToggle = function () {
    if (!this.toggle) return;
    var self = this;
    this.updateToggleVisibility();

    this.toggle.addEventListener('click', function () {
      self.pausedByUser = !self.pausedByUser;
      self.toggle.setAttribute('aria-pressed', String(self.pausedByUser));
      self.toggle.setAttribute(
        'aria-label',
        self.pausedByUser ? 'Play banner slideshow' : 'Pause banner slideshow'
      );

      self.syncMedia();
    });
  };

  /**
   * 整张 slide 是一个 <a>，手机上横滑会被判成点击直接跳走。
   * 拖动一开始就打标记，标记还在时吞掉 click，下一次 touchStart 再清掉。
   */
  BannerSlider.prototype.bindDragGuard = function () {
    if (!this.swiper) return;
    var dragged = false;

    this.swiper.on('touchStart', function () {
      dragged = false;
    });
    this.swiper.on('sliderFirstMove', function () {
      dragged = true;
    });

    // 监听器挂在 viewport 而不是 root，且只吞 slide 自己的 click：
    // 箭头和暂停按钮在 .swiper 之外，点它们不会触发 swiper 的 touchStart，
    // 挂在 root 上的话 dragged 永远没机会复位，用户滑一次之后
    // 箭头/暂停/键盘激活就全被吞掉了。
    // 拦截后立刻复位，不指望下一次 touchStart 来清。
    this.viewport.addEventListener(
      'click',
      function (event) {
        if (!dragged) return;
        dragged = false;
        if (!event.target.closest('[data-hb-slide]')) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  };

  /* ---------------------------------------------------------------- Tracking */

  /*
   * 这里刻意不推任何自定义 dataLayer 事件。
   * 曝光和点击都已经在线上跑着，走的是 GTM 容器侧：
   *   home_banner_view  —— Element Visibility 触发器，Once per element
   *   home_banner_click —— Just Links 触发器
   * 两者读的都是元素上的 data-banner-* 属性，这 5 个维度
   * （banner_id / name / position / url / text）已在 GA4 注册。
   * 见 docs/tracking-contract-data-track.md §5.3：「切勿另造，造成漂移」。
   *
   * 轮播天然满足 Element Visibility 的口径：非当前 slide 被 swiper 平移到
   * 视口外，IntersectionObserver 判定为不可见；滑到哪一屏哪一屏才计一次曝光。
   * 所以 slide 上照旧带齐 data-gtm-banner / data-banner-* 即可，无需主题侧上报。
   *
   * 新增的 data-banner-slot（第几屏）是这次多出来的维度，
   * 想按屏拆 CTR 的话需要在 GTM 里加一个 AEV 变量读它，并在 GA4 注册。
   */

  BannerSlider.prototype.observeVisibility = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      return;
    }

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.inView = entry.isIntersecting;
          self.syncMedia();
        });
      },
      { threshold: 0.4 }
    );

    this.observer.observe(this.root);
  };

  /* --------------------------------------------------------------- Lifecycle */

  BannerSlider.prototype.destroy = function () {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.mobileMQ.removeEventListener) {
      this.mobileMQ.removeEventListener('change', this.onViewportChange);
    } else if (this.mobileMQ.removeListener) {
      this.mobileMQ.removeListener(this.onViewportChange);
    }

    if (this.observer) this.observer.disconnect();
    if (this.swiper) this.swiper.destroy(true, true);
  };

  /* -------------------------------------------------------------------- Boot */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-hb-slider]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.hbInit === '1') return;
      root.dataset.hbInit = '1';
      instances.push(new BannerSlider(root));
    });
  }

  function find(root) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root === root) return instances[i];
    }
    return null;
  }

  function boot() {
    if (typeof window.Swiper === 'undefined') {
      // swiper-bundle 也是 defer，正常情况下先于本文件执行；兜底再等一轮。
      window.addEventListener('load', function () {
        init(document);
      });
      return;
    }
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------------------------------------------------- Theme editor support */

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-hb-slider]');
    if (!root) return;
    var instance = find(root);
    if (instance) {
      instance.destroy();
      instances.splice(instances.indexOf(instance), 1);
    }
  });

  // 选中某个 slide 的 block 时跳到那一屏并停下，否则运营改不了第二个活动。
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-hb-slider]');
    if (!root) return;
    var instance = find(root);
    if (!instance || !instance.swiper) return;

    var index = instance.slides.indexOf(event.target);
    if (index > -1) instance.swiper.slideTo(index);
    if (instance.swiper.autoplay) instance.swiper.autoplay.stop();
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target.closest('[data-hb-slider]');
    if (!root) return;
    var instance = find(root);
    if (!instance || !instance.swiper || !instance.swiper.autoplay) return;
    if (instance.autoplayEnabled && !instance.pausedByUser) instance.swiper.autoplay.start();
  });
})();
